import { defineMetrics } from "../types.js"

/**
 * ⚠ Two candidate associations again, and the wrong one is wrong very subtly:
 * - `dmsContainer.tag` is each container's exact tag, and `dmsDocument.parent` is `required`
 * and `immutable`. Use those two.
 * - `dmsTag.allContainers[]` looks equivalent but is **rolled up to the root tag**: the
 * container's pre-save hook pushes to `parents[0]` when the tag has a parent. Using it books
 * a child tag's documents against its parent — so every leaf tag reads 0 and the root reads
 * the whole subtree.
 */
export const tagsMetrics = defineMetrics([
  {
    id: "total_system_tags",
    name: "Total System Tags",
    description: "Total number of system-generated tags.",
    logic: "Count all system tag records.",
    source: "dmsTag(isSystem = true) — not the dmsSystemTag catalogue",
    domain: "tags",
    srvModule: "tags",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["line"],
    coveredByV1: false,
    xlsxRow: 28,
  },
  {
    id: "documents_per_system_tag",
    name: "Documents per System Tag",
    description: "Number of documents assigned to each system tag.",
    logic: "Count document-tag relationships for each system tag.",
    source: "dmsDocument.parent -> dmsContainer.tag, folded onto system tags",
    domain: "tags",
    srvModule: "tags",
    priority: "must",
    valueType: "count",
    dims: ["entity"],
    rollup: "last",
    defaultWidget: "hbar",
    altWidgets: ["table", "bar"],
    coveredByV1: false,
    caveat:
      "Documents reach tags through their container, since DMSDocument has no tag field. Uses dmsContainer.tag rather than dmsTag.allContainers, because the latter is rolled up to the root tag and would report 0 for every child tag while crediting the whole subtree to its parent. A tag holding no documents is reported as 0 rather than omitted, since an unused tag is itself the finding.",
    xlsxRow: 29,
  },
  {
    id: "total_user_tags",
    name: "Total User Tags",
    description: "Total number of user-created tags.",
    logic: "Count all user-generated tags.",
    source: "dmsTag(isSystem != true)",
    domain: "tags",
    srvModule: "tags",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["line"],
    coveredByV1: false,
    xlsxRow: 30,
  },
  {
    id: "documents_per_user_tag",
    name: "Documents per User Tag",
    description: "Number of documents assigned to each user tag.",
    logic: "Count document-tag relationships for each user tag.",
    source: "dmsDocument.parent -> dmsContainer.tag, folded onto user tags",
    domain: "tags",
    srvModule: "tags",
    priority: "must",
    valueType: "count",
    dims: ["entity"],
    rollup: "last",
    defaultWidget: "hbar",
    altWidgets: ["table", "bar"],
    coveredByV1: false,
    xlsxRow: 31,
  },
])
